import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Drawer, Icon, Typography} from '@mui/material';
import {BODY1, CAPTION, H6, IMAGE_PREVIEW_ALT, OUTLINE, SUBTITLE1, TEXT} from '@/utils/constants';
import {ReactComponent as ArrowRightIcon} from '@icons/arrow-line-right.svg';
import {ReactComponent as ArrowLeftIcon} from '@icons/arrow-line-left.svg';
import {ReactComponent as TaskIcon} from '@icons/task.svg';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {ReactComponent as PaymentIcon} from '@icons/payment-method.svg';
import {
  getAuction,
  getAuctionCategories,
  getAuctionCategoriesSelector,
  getAuctionList,
  getAuctionListSelector,
  getAuctionSelector,
} from '@/store/organization-auction';
import {AUCTION_STATUS, AUCTION_TYPE, PAYMENT_METHOD} from '@/utils/enums';
import {formContentStyle} from '@/components/Organization/Dashboard/CreateNewEvent/EventSetting/FormContent/FormContent.styles';
import Button from '@/shared/components/Button';
import Modal from '@/shared/components/Modal';
import AddBidModal from '@/components/User/LotItem/AddBidModal';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {itemDetailPageStyle} from './ItemDetailPages.style';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import Loader from '@/shared/components/Loader';
import {IOrganizationAuctionBidData, IOrganizationAuctionBidResponse, PaymentResult} from '@/utils/types';
import {OrganizationAttendeesService, OrganizationAuctionService} from '@/utils/services';
import {UserBidService} from '@/utils/services/user-bid.service';
import {getCurrentEventSelector} from '@/store/organization-event';
import Placeholder from '@/assets/images/plh.png';
import {getUserInfo, getUserSelector} from '@/store/user';
import {useAuctionTimer} from '@/utils/hooks';
import {currencyFormatter} from '@/utils/helpers/currency';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {userAPI} from '@/core/api';
import PaymentOptions from '../Profile/General/PaymentOptions';
import {profileContactList} from '../Profile/General/ProfileGeneral.style';
import { useCardAttachment, WaitResult } from '@/utils/hooks/useCardAttachment';
import axios from 'axios';

const getCardErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if ((error.response?.status ?? 0) >= 500) {
      return 'Something went wrong on our side. Please try again later.';
    }
    return getErrorMessage(error);
  }
  return error instanceof Error ? error.message : 'Something went wrong. Please try again later.';
};

const ItemDetailPage: FC = () => {
  const {id} = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const auctionType = location.state?.from;

  const user = useSelector(getUserSelector);
  const event = useAppSelector(getCurrentEventSelector);
  const auctionItem = useSelector(getAuctionSelector);
  const auctionList = useAppSelector(getAuctionListSelector);
  const auctionCategories = useAppSelector(getAuctionCategoriesSelector);

  const {isStarting, isWaiting, waitForAttachment, startAttachment} = useCardAttachment(event?.id, location.pathname);

  const [isBidModalOpen, setIsBidModalOpen] = useState(false);
  const [bidsData, setBidsData] = useState<IOrganizationAuctionBidResponse>();
  const [lastData, setLastData] = useState<IOrganizationAuctionBidData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [amount, setAmount] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [isShowPaymentModal, setIsShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState<1 | 2>(1);
  const [paymentOption, setPaymentOption] = useState(
    user?.paymentMethod ? user.paymentMethod : PAYMENT_METHOD.CREDIT_CARD
  );

  const {apiInstance} = useApiInstance();
  const {hasEnded} = useAuctionTimer(event?.endAuctionData);

  const isOffline = event?.isOnlineAuction === false;
  const isClosed = hasEnded || auctionItem?.status === AUCTION_STATUS.CLOSED;

  const canPlaceBid = !isOffline && !isClosed;
  const canBuyNow = canPlaceBid && bidsData?.totalCount === 0;

  const getBidTitle = () => {
    if (isOffline) return 'Offline Auction Only';
    if (isClosed) return 'Auction Ended';
    return 'Place Bid';
  };

  useEffect(() => {
    if (event?.id) {
      getAuctionListData();
    }
  }, [page, event?.id, auctionType]);

  useEffect(() => {
    if (auctionList?.currentPage && auctionList.currentPage !== page) {
      setPage(auctionList.currentPage);
    }
  }, [auctionList?.currentPage]);

  useEffect(() => {
    if (user?.paymentMethod) setPaymentOption(user.paymentMethod);
  }, [user?.paymentMethod]);

  // NEW: тост обробки картки
  useEffect(() => {
    if (!isWaiting) return undefined;

    const toastId = toast.loading('We are processing your card. This may take a few seconds...');

    return () => {
      toast.dismiss(toastId);
    };
  }, [isWaiting]);

  const handleNextStep = () => setPaymentStep(2);
  const handleClosePaymentModal = () => {
    setIsShowPaymentModal(false);
    setPaymentStep(1);
  };

  // ---------- Payment: старе (не-карткові способи) ----------
  const addPaymentOption = async () => {
    if (!user?.bidderId || !user?.id) return;
    try {
      const res = await OrganizationAttendeesService.addAttendeePaymentStatus(user.bidderId, paymentOption);
      if (res.status === 204) {
        await dispatch(getUserInfo(user?.id)).unwrap();
        toast.success('Payment method added successfully!');
      }

      handleClosePaymentModal();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ---------- Payment: Stripe-флоу для картки (NEW) ----------
  const syncUser = async () => {
    if (user?.id) await dispatch(getUserInfo(user.id)).unwrap();
  };

  const handleWaitResult = async (result: WaitResult) => {
    if (result === 'attached') {
      await syncUser();
      toast.success('Payment method added successfully!', {toastId: 'card-attached'});
    }
    if (result === 'timeout') {
      toast.error('Card verification is taking longer than expected. Please refresh the page in a moment.', {
        toastId: 'card-timeout',
        autoClose: 8000,
      });
    }
  };

  const addCardViaStripe = async () => {
    try {
      const result = await startAttachment();
      if (result === 'redirect') return;

      handleClosePaymentModal();

      if (result === 'attached') {
        await syncUser();
        toast.success('Payment method is already attached');
        return;
      }

      // processing
      await handleWaitResult(await waitForAttachment());
    } catch (error) {
      handleClosePaymentModal();
      toast.error(getCardErrorMessage(error), {toastId: 'card-setup-error', autoClose: 8000});
    }
  };

  const handleConfirmPayment = () =>
    paymentOption === PAYMENT_METHOD.CREDIT_CARD ? addCardViaStripe() : addPaymentOption();

  const getAuctionListData = async () => {
    try {
      const reqBody = {
        eventId: event?.id as number,
        page,
        auctionType,
        pageSize: 8,
      };

      await dispatch(getAuctionList(reqBody)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const categoryName = useMemo(
    () => auctionCategories?.find((auctionCategory) => auctionCategory.id === auctionItem?.categoryId)?.name,
    [auctionCategories, auctionItem?.categoryId]
  );

  const liveAuctionTitle = auctionItem?.isLiveAuction ? AUCTION_TYPE.LIVE : AUCTION_TYPE.SILENT;

  const handleAddBid = async () => {
    const numericAmount = parseFloat(amount.replace(',', '.'));

    if (!id || !user) return;

    if (amount === '' || numericAmount <= 0) {
      toast.error('Bid amount should be greater than 0');
      return;
    }

    try {
      const dataReq = {
        itemId: +id,
        bidderId: user?.bidderId,
        eventId: event?.id as number,
        amount: numericAmount,
        isSetByOrganizer: true,
      };

      const res = await UserBidService.createBid(dataReq);

      if (res.status === 204) {
        toast.success('Bid placed successfully');
        await getAuctionBids();
        await getAuctionData();
        setIsBidModalOpen(false);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBidNowClick = () => {
    if (hasEnded) {
      toast.error('The auction has ended. You can no longer place a bid.');
      return;
    }
    if (!user?.paymentMethod) {
      setIsShowPaymentModal(true);
      return;
    }
    setIsBidModalOpen(true);
    setAmount('');
  };

  const handleBuyNowClick = async () => {
    if (!id || !user) return;

    if (hasEnded) {
      toast.error('The auction has ended. You can no longer place a bid.');
      return;
    }
    if (!user?.paymentMethod) {
      setIsShowPaymentModal(true);
      return;
    }

    try {
      const {data} = await UserBidService.buyItem({
        itemId: +id,
        bidderId: user?.bidderId,
        eventId: event?.id as number,
      });

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const elIndex = auctionList?.data?.findIndex((item) => item.id === auctionItem?.id) ?? -1;
  const totalPages = auctionList?.totalPages ?? 1;
  const currentPage = auctionList?.currentPage || page;

  const isPreviousDisabled = !auctionList?.data || (elIndex <= 0 && currentPage <= 1);
  const isNextDisabled = !auctionList?.data || (elIndex === auctionList.data.length - 1 && currentPage >= totalPages);

  const handleNextClick = async () => {
    if (!event?.id || !auctionItem) return;

    if (auctionList?.data && elIndex !== -1 && elIndex < auctionList.data.length - 1) {
      const nextItem = auctionList.data[elIndex + 1];
      navigate(`/${event.customizeWebpage}/details/${nextItem.id}`, {state: {from: auctionType}});
      return;
    }

    const currentPage = auctionList?.currentPage || page;
    if (auctionList?.totalPages && currentPage < auctionList.totalPages) {
      const nextPage = currentPage + 1;
      try {
        const nextPageData = await dispatch(
          getAuctionList({
            eventId: event?.id as number,
            page: nextPage,
            auctionType,
            pageSize: 8,
          })
        ).unwrap();
        if (nextPageData?.data && nextPageData.data.length > 0) {
          setPage(nextPage);
          navigate(`/${event.customizeWebpage}/details/${nextPageData.data[0].id}`, {state: {from: auctionType}});
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const handlePreviousClick = async () => {
    if (!event?.id || !auctionItem) return;

    // If current item is in the list and there's a previous item on the same page
    if (auctionList?.data && elIndex !== -1 && elIndex > 0) {
      const prevItem = auctionList.data[elIndex - 1];
      navigate(`/${event.customizeWebpage}/details/${prevItem.id}`, {state: {from: auctionType}});
      return;
    }

    // Check if there's a previous page
    const currentPage = auctionList?.currentPage || page;
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      try {
        const prevPageData = await dispatch(
          getAuctionList({
            eventId: event?.id as number,
            page: prevPage,
            auctionType,
            pageSize: 8,
          })
        ).unwrap();
        if (prevPageData?.data && prevPageData.data.length > 0) {
          setPage(prevPage);
          const lastItem = prevPageData.data[prevPageData.data.length - 1];
          navigate(`/${event.customizeWebpage}/details/${lastItem.id}`, {state: {from: auctionType}});
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const getAuctionCategoriesData = async () => {
    try {
      await dispatch(getAuctionCategories(apiInstance)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (!auctionCategories) getAuctionCategoriesData();
  }, []);

  const getAuctionData = async () => {
    if (!id) return;

    setIsLoading(true);

    try {
      await dispatch(getAuction({id: +id, apiInstance})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAuctionData();
  }, [id]);

  useEffect(() => {
    getAuctionBids();
  }, [id]);

  const getAuctionBids = async () => {
    if (!id) return;

    try {
      const reqBody = {
        page: 1,
        itemId: +id,
      };

      const bidsResponse = await OrganizationAuctionService.getAuctionBids(reqBody);
      setBidsData(bidsResponse.data);

      if (bidsResponse.data.totalPages > 1) {
        const lastResponse = await OrganizationAuctionService.getAuctionBids({
          ...reqBody,
          page: bidsResponse.data.totalPages,
        });
        setLastData(lastResponse.data.data[lastResponse?.data?.data.length - 1]);
      } else {
        setLastData(bidsResponse.data.data[bidsResponse?.data?.data.length - 1]);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const MAX_RETRIES = 5;

  const checkPaymentStatus = async (paymentId: string, retryCount = 0, toastId?: string | number) => {
    if (retryCount === 0) {
      toastId = toast.loading('Checking payment status...');
    }

    try {
      const {data} = await OrganizationStripeService.checkPaymentStatus(+paymentId, userAPI);

      if (data.status === PaymentResult.COMPLETED) {
        if (toastId) toast.dismiss(toastId);
        toast.success('Payment completed successfully!');
        await getAuctionData();
        await getAuctionBids();
      } else if (data.status === PaymentResult.PENDING) {
        if (retryCount < MAX_RETRIES) {
          timeoutRef.current = setTimeout(() => {
            checkPaymentStatus(paymentId, retryCount + 1, toastId);
          }, 3000);
        } else {
          if (toastId) toast.dismiss(toastId);
          toast.info('Payment is still being processed. Please check the status in a few minutes.');
        }
      } else if (data.status === PaymentResult.FAILED) {
        if (toastId) toast.dismiss(toastId);
        toast.error('Payment failed!');
      } else if (data.status === PaymentResult.REFUNDED) {
        if (toastId) toast.dismiss(toastId);
        toast.error('Payment refunded!');
      }
    } catch (error) {
      if (toastId) toast.dismiss(toastId);
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get('paymentId');

    if (paymentId) {
      checkPaymentStatus(paymentId);

      params.delete('paymentId');
      params.delete('session_id');
      params.delete('organizationId');

      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    return () => clearTimeout(timeoutRef.current);
  }, []);

  return (
    <Box sx={itemDetailPageStyle.wrapper}>
      <Box sx={itemDetailPageStyle.wrapperHeader}>
        <Button
          sx={itemDetailPageStyle.button}
          title="Previous item"
          variant={TEXT}
          startIcon={
            <Icon sx={[itemDetailPageStyle.icon, isPreviousDisabled && itemDetailPageStyle.iconDisable]}>
              <ArrowLeftIcon />
            </Icon>
          }
          onClick={handlePreviousClick}
          disabled={isPreviousDisabled}
        />
        <Button
          sx={itemDetailPageStyle.button}
          title="Back to Items"
          variant={TEXT}
          startIcon={
            <Icon sx={itemDetailPageStyle.icon}>
              <TaskIcon />
            </Icon>
          }
          onClick={() => navigate(`/${event?.customizeWebpage}/AllItems`)}
        />
        <Button
          sx={itemDetailPageStyle.button}
          title="Next item"
          variant={TEXT}
          endIcon={
            <Icon sx={[itemDetailPageStyle.iconRight, isNextDisabled && itemDetailPageStyle.iconDisable]}>
              <ArrowRightIcon />
            </Icon>
          }
          onClick={handleNextClick}
          disabled={isNextDisabled}
        />
      </Box>

      <Box sx={itemDetailPageStyle.wrapperContent}>
        {isLoading ? (
          <Box sx={{height: 'calc(100vh - 270px)'}}>
            <Loader sx={formContentStyle.loader} size={50} />
          </Box>
        ) : (
          <>
            <Box sx={itemDetailPageStyle.logosWrapper}>
              <Box sx={itemDetailPageStyle.content}>
                {auctionItem?.itemImages && auctionItem?.itemImages.length > 0 ? (
                  auctionItem?.itemImages.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        ...itemDetailPageStyle.donorLogo,
                        '&:first-of-type:before': {
                          content: `"${liveAuctionTitle}"`,
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          backgroundColor: '#fff',
                          color: 'var(--primary-500)',
                          borderRadius: '50px',
                          padding: '4px 12px',
                          fontSize: '12px',
                        },
                      }}
                    >
                      <img src={item.image} alt={IMAGE_PREVIEW_ALT} />
                    </Box>
                  ))
                ) : (
                  <Box sx={itemDetailPageStyle.altContent}>
                    <img src={Placeholder} alt={IMAGE_PREVIEW_ALT} />
                  </Box>
                )}
              </Box>
              <Box sx={itemDetailPageStyle.contentWrapper}>
                <Box sx={itemDetailPageStyle.title}>
                  <Typography variant={H6}>{auctionItem?.name}</Typography>
                  <Typography variant={BODY1} sx={itemDetailPageStyle.category}>
                    {categoryName}
                  </Typography>
                </Box>

                {auctionItem && !auctionItem.isLiveAuction && (
                  <Box sx={itemDetailPageStyle.valuesWrapper}>
                    <Box sx={itemDetailPageStyle.bidWrapper}>
                      <Typography sx={itemDetailPageStyle.bid} variant={CAPTION}>
                        <Typography variant={SUBTITLE1}>{bidsData?.totalCount || 0}</Typography> Bids
                      </Typography>
                      <Typography sx={itemDetailPageStyle.bid} variant={CAPTION}>
                        <Typography variant={SUBTITLE1}>
                          {currencyFormatter.format(lastData?.bid || auctionItem?.minBid)}
                        </Typography>
                        Current bid
                      </Typography>
                    </Box>

                    <Box sx={itemDetailPageStyle.buttonWrapper}>
                      {auctionItem?.winnerDetails ? (
                        <Button title="Sold" variant={OUTLINE} disabled />
                      ) : (
                        <>
                          {auctionItem.buyNowPrice ? (
                            <Button
                              title={`Buy now for ${currencyFormatter.format(auctionItem?.buyNowPrice)}`}
                              variant={OUTLINE}
                              onClick={handleBuyNowClick}
                              disabled={!canBuyNow}
                            />
                          ) : null}
                          <Button title={getBidTitle()} onClick={handleBidNowClick} disabled={!canPlaceBid} />
                        </>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
            {auctionItem?.description && (
              <Box sx={itemDetailPageStyle.description}>
                <Typography variant={SUBTITLE1}>Description</Typography>
                <Typography variant={BODY1}>{auctionItem?.description}</Typography>
              </Box>
            )}
            <Box>
              {auctionItem && auctionItem?.donors.length > 0 && <Typography variant={SUBTITLE1}>Donated By</Typography>}
              <Box sx={itemDetailPageStyle.donorImages}>
                {auctionItem &&
                  auctionItem?.donors.map((donor) => (
                    <Box key={donor.id} sx={itemDetailPageStyle.donorImage}>
                      <img src={donor.logoUrl} alt={IMAGE_PREVIEW_ALT} />
                      <Typography sx={itemDetailPageStyle.donorName}>{donor.name}</Typography>
                    </Box>
                  ))}
              </Box>
            </Box>
          </>
        )}
        <Drawer variant="permanent" anchor="bottom" sx={itemDetailPageStyle.drawer}>
          <Box sx={itemDetailPageStyle.popup}>
            <Button
              sx={itemDetailPageStyle.button}
              title="Previous item"
              variant={TEXT}
              startIcon={
                <Icon sx={[itemDetailPageStyle.icon, isPreviousDisabled && itemDetailPageStyle.iconDisable]}>
                  <ArrowLeftIcon />
                </Icon>
              }
              onClick={handlePreviousClick}
              disabled={isPreviousDisabled}
            />

            <Button
              sx={itemDetailPageStyle.button}
              title="Next item"
              variant={TEXT}
              endIcon={
                <Icon sx={[itemDetailPageStyle.iconRight, isNextDisabled && itemDetailPageStyle.iconDisable]}>
                  <ArrowRightIcon />
                </Icon>
              }
              onClick={handleNextClick}
              disabled={isNextDisabled}
            />
          </Box>
        </Drawer>
      </Box>
      <Modal
        isShowSaveButton
        mainButtonTitle="Place Bid"
        isShowCloseButton
        closeButtonTitle="Cancel"
        onClose={() => setIsBidModalOpen(false)}
        open={isBidModalOpen}
        sx={itemDetailPageStyle.modalBid}
        onSave={handleAddBid}
      >
        <AddBidModal
          amount={amount}
          setAmount={setAmount}
          totalBids={bidsData?.totalCount || 0}
          maxBid={lastData?.bid || 0}
          bidIncrement={auctionItem?.bidIncrement || 0}
        />
      </Modal>

      {paymentStep === 1 ? (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Add Payment Method"
          description="Adding a payment method allows you to participate in the auction and make donations."
          isShowCloseButton
          closeButtonTitle="Later"
          mainButtonTitle="Add Payment Method"
          isShowSaveButton
          onSave={handleNextStep}
          mainIcon={<PaymentIcon />}
          sx={profileContactList.modal}
        />
      ) : (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Choose Payment Option"
          isShowCloseButton
          closeButtonTitle="Cancel"
          mainButtonTitle="Confirm"
          isShowSaveButton
          isSaveButtonDisable={isStarting}
          onSave={handleConfirmPayment}
          sx={profileContactList.modal}
        >
          <PaymentOptions value={paymentOption} onChange={setPaymentOption} />
        </Modal>
      )}
    </Box>
  );
};

export default ItemDetailPage;
